import { Divider } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { MuiTelInput } from 'mui-tel-input';
import useGetTravelDocument from '../../../hooks/useTravelDocuments';
import { useAuth0 } from '../../../auth/AuthProvider';
import useGetDiveInsurance from '../../../hooks/useGetDiveInsurance';
import useGetEmergencyContacts from '../../../hooks/useGetEmergencyContact';
import useUpsertDiveInsurance from '../../../hooks/useUpsertDiveInsurance';
import useUpsertEmergencyContact from '../../../hooks/useUpsertEmergencyContact';
import useUpsertTravelDocument from '../../../hooks/useUpsertTravelDocument';
import { formatInputDate } from '../../../common/utilities/dateFormatter';
import { displayEmpty } from '../../../common/utilities/displayEmpty';
import {
  isEmergencyContactComplete,
  isPassportComplete,
} from '../utilities/profileCompleteness';
import ProfileRequiredBadge from '../components/ProfileRequiredBadge';
import ProfileSubpageState from '../components/ProfileSubpageState';

const emptyPassport = {
  countryOfIssuance: '',
  passportNumber: '',
  expiryDate: '',
};

const emptyEmergencyContact = {
  fullName: '',
  phone: '',
};

const emptyDiveInsurance = {
  policyNumber: '',
};

const TravelDocuments = () => {
  const { user } = useAuth0();
  const userId = user?.sub ?? '';

  const {
    travelDocument: travelDocumentData,
    isLoading: travelDocumentLoading,
    isError: travelDocumentError,
    refetch: refetchTravelDocument,
    isFetching: travelDocumentFetching,
  } = useGetTravelDocument(userId, true);

  const {
    emergencyContacts: emergencyContactsData,
    isLoading: emergencyContactsLoading,
    isError: emergencyContactsError,
    refetch: refetchEmergencyContacts,
    isFetching: emergencyContactsFetching,
  } = useGetEmergencyContacts(userId, true);

  const {
    diveInsurance: diveInsuranceData,
    isLoading: diveInsuranceLoading,
    isError: diveInsuranceError,
    refetch: refetchDiveInsurance,
    isFetching: diveInsuranceFetching,
  } = useGetDiveInsurance(userId, true);

  const [passportEditMode, setPassportEditMode] = useState(false);
  const [emergencyContactEditMode, setEmergencyContactEditMode] =
    useState(false);
  const [diveInsuranceEditMode, setDiveInsuranceEditMode] = useState(false);

  const [passportDetails, setPassportDetails] = useState(emptyPassport);
  const [emergencyContact, setEmergencyContact] = useState(
    emptyEmergencyContact,
  );
  const [diveInsurance, setDiveInsurance] = useState(emptyDiveInsurance);

  const upsertTravelDocument = useUpsertTravelDocument();
  const upsertEmergencyContact = useUpsertEmergencyContact();
  const upsertDiveInsurance = useUpsertDiveInsurance();

  const syncPassportFromServer = () => {
    setPassportDetails({
      countryOfIssuance: travelDocumentData?.countryOfIssuance || '',
      passportNumber: travelDocumentData?.passportNumber || '',
      expiryDate: travelDocumentData?.expiryDate || '',
    });
  };

  const syncEmergencyContactFromServer = () => {
    setEmergencyContact({
      fullName: emergencyContactsData?.fullName || '',
      phone: emergencyContactsData?.phone || '',
    });
  };

  const syncDiveInsuranceFromServer = () => {
    setDiveInsurance({
      policyNumber: diveInsuranceData?.policyNumber || '',
    });
  };

  useEffect(() => {
    if (!passportEditMode) {
      syncPassportFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when server data or edit mode changes
  }, [travelDocumentData, passportEditMode]);

  useEffect(() => {
    if (!emergencyContactEditMode) {
      syncEmergencyContactFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emergencyContactsData, emergencyContactEditMode]);

  useEffect(() => {
    if (!diveInsuranceEditMode) {
      syncDiveInsuranceFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diveInsuranceData, diveInsuranceEditMode]);

  const handleCancelPassport = () => {
    syncPassportFromServer();
    setPassportEditMode(false);
  };

  const handleCancelEmergencyContact = () => {
    syncEmergencyContactFromServer();
    setEmergencyContactEditMode(false);
  };

  const handleCancelDiveInsurance = () => {
    syncDiveInsuranceFromServer();
    setDiveInsuranceEditMode(false);
  };

  const handleSaveTravelDocument = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await upsertTravelDocument.mutateAsync({
        documentId: travelDocumentData?.documentId || -1,
        userId,
        ...passportDetails,
      });
      setPassportEditMode(false);
    } catch {
      // Keep edit mode open; toast handled by mutation feedback.
    }
  };

  const handleSaveEmergencyContact = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await upsertEmergencyContact.mutateAsync({
        contactId: emergencyContactsData?.contactId || -1,
        userId,
        ...emergencyContact,
      });
      setEmergencyContactEditMode(false);
    } catch {
      // Keep edit mode open.
    }
  };

  const handleSaveDiveInsurance = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await upsertDiveInsurance.mutateAsync({
        insuranceId: diveInsuranceData?.insuranceId || -1,
        userId,
        policyNumber: diveInsurance.policyNumber,
      });
      setDiveInsuranceEditMode(false);
    } catch {
      // Keep edit mode open.
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPassportDetails(prevDetails => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  if (
    travelDocumentLoading ||
    emergencyContactsLoading ||
    diveInsuranceLoading ||
    travelDocumentError ||
    emergencyContactsError ||
    diveInsuranceError
  ) {
    const hasError =
      travelDocumentError || emergencyContactsError || diveInsuranceError;

    return (
      <ProfileSubpageState
        title="Travel documents"
        isLoading={
          travelDocumentLoading ||
          emergencyContactsLoading ||
          diveInsuranceLoading
        }
        isError={hasError}
        errorMessage="We couldn’t load your travel documents. Please try again."
        onRetry={() => {
          void Promise.all([
            refetchTravelDocument(),
            refetchEmergencyContacts(),
            refetchDiveInsurance(),
          ]);
        }}
        isRetrying={
          hasError &&
          (travelDocumentFetching ||
            emergencyContactsFetching ||
            diveInsuranceFetching)
        }
      />
    );
  }

  const isPassportRequired = !isPassportComplete(passportDetails);

  const isEmergencyContactRequired = !isEmergencyContactComplete({
    emergencyContactName: emergencyContact.fullName,
    emergencyContactPhone: emergencyContact.phone,
  });

  return (
    <div className="px-6 md:px-0 md:min-w-[772px]">
      <div className="flex flex-col">
        <span className="text-2xl md:text-3xl font-semibold text-neutral-800">
          Travel documents
        </span>
        <span className="mt-2 text-base text-neutral-500">
          Provide your passport information to book your national park entrance
        </span>
      </div>
      <form onSubmit={handleSaveTravelDocument} className="mt-6">
        <div className="flex justify-between font-semibold">
          <div className="flex items-center">
            <span className="text-xl">Passport</span>
            {passportEditMode && isPassportRequired && <ProfileRequiredBadge />}
          </div>
          <button
            type="button"
            className="underline"
            onClick={() =>
              passportEditMode
                ? handleCancelPassport()
                : setPassportEditMode(true)
            }
          >
            {passportEditMode ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="flex flex-col mt-4">
          <span className="text-neutral-500">Country of issuance</span>
          {passportEditMode ? (
            <input
              type="text"
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
              name="countryOfIssuance"
              value={passportDetails.countryOfIssuance}
              onChange={handleInputChange}
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(passportDetails.countryOfIssuance)}
            </span>
          )}
        </div>
        <Divider />
        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Passport number</span>
          {passportEditMode ? (
            <input
              type="text"
              name="passportNumber"
              value={passportDetails.passportNumber}
              onChange={handleInputChange}
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(passportDetails.passportNumber)}
            </span>
          )}
        </div>
        <Divider />
        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Expiry date</span>
          {passportEditMode ? (
            <input
              type="date"
              name="expiryDate"
              value={formatInputDate(passportDetails.expiryDate)}
              onChange={handleInputChange}
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(formatInputDate(passportDetails.expiryDate))}
            </span>
          )}
        </div>
        {passportEditMode && (
          <button
            type="submit"
            disabled={upsertTravelDocument.isPending}
            className="mt-4 bg-brand-secondary text-neutral-00 w-[120px] rounded-lg px-6 py-4 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {upsertTravelDocument.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>
      <form onSubmit={handleSaveEmergencyContact} className="mt-6">
        <div className="flex justify-between font-semibold">
          <div className="flex items-center">
            <span className="text-xl">Emergency contacts</span>
            {emergencyContactEditMode && isEmergencyContactRequired && (
              <ProfileRequiredBadge />
            )}
          </div>
          <button
            type="button"
            className="underline"
            onClick={() =>
              emergencyContactEditMode
                ? handleCancelEmergencyContact()
                : setEmergencyContactEditMode(true)
            }
          >
            {emergencyContactEditMode ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="flex flex-col mt-4">
          <span className="text-neutral-500">Full name</span>
          {emergencyContactEditMode ? (
            <input
              type="text"
              name="fullName"
              value={emergencyContact.fullName}
              onChange={event =>
                setEmergencyContact({
                  ...emergencyContact,
                  fullName: event.target.value,
                })
              }
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(emergencyContact.fullName)}
            </span>
          )}
        </div>
        <Divider />
        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Phone number</span>
          {emergencyContactEditMode ? (
            <div className="mb-4 mt-4">
              <MuiTelInput
                fullWidth
                value={emergencyContact.phone}
                onChange={value =>
                  setEmergencyContact({
                    ...emergencyContact,
                    phone: value,
                  })
                }
                aria-label="Emergency contact phone number"
              />
            </div>
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(emergencyContact.phone)}
            </span>
          )}
        </div>

        {emergencyContactEditMode && (
          <button
            type="submit"
            disabled={upsertEmergencyContact.isPending}
            className="mt-4 bg-brand-secondary text-neutral-00 w-[120px] rounded-lg px-6 py-4 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {upsertEmergencyContact.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>
      <form onSubmit={handleSaveDiveInsurance} className="mt-6">
        <div className="flex justify-between font-semibold">
          <span className="text-xl">Dive insurance (optional)</span>
          <button
            type="button"
            className="underline"
            onClick={() =>
              diveInsuranceEditMode
                ? handleCancelDiveInsurance()
                : setDiveInsuranceEditMode(true)
            }
          >
            {diveInsuranceEditMode ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="flex flex-col mt-4">
          <span className="text-neutral-500">Policy number</span>
          {diveInsuranceEditMode ? (
            <input
              type="text"
              name="policyNumber"
              value={diveInsurance.policyNumber}
              onChange={event =>
                setDiveInsurance({
                  ...diveInsurance,
                  policyNumber: event.target.value,
                })
              }
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(diveInsurance.policyNumber)}
            </span>
          )}
        </div>
        {diveInsuranceEditMode && (
          <button
            type="submit"
            disabled={upsertDiveInsurance.isPending}
            className="mt-4 bg-brand-secondary text-neutral-00 w-[120px] rounded-lg px-6 py-4 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {upsertDiveInsurance.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>
    </div>
  );
};

export default TravelDocuments;
